import { useState, useEffect, useRef, useCallback } from 'react';

export interface UseTimerOptions {
  initialCount: number;
  onComplete?: () => void;
  onTick?: (count: number) => void;
  autoStart?: boolean;
  interval?: number;
  isEnabled: boolean;

}

export interface UseTimerReturn {
  count: number;
  isRunning: boolean;
  startTimer: () => void;
  stopTimer: () => void;
  pauseTimer: () => void;
  resetTimer: () => void;
}

const useTimer = ({
  initialCount,
  onComplete,
  onTick,
  autoStart = false,
  interval = 1000,
  isEnabled = true,
}: UseTimerOptions): UseTimerReturn => {
  const [count, setCount] = useState(initialCount);
  const [isRunning, setIsRunning] = useState(autoStart);
  const hasStartedRef = useRef(autoStart);

  const onCompleteRef = useRef(onComplete);
  const onTickRef = useRef(onTick);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onTickRef.current = onTick;
  }, [onComplete, onTick]);

  useEffect(() => {
    if (!hasStartedRef.current && !isRunning) {
      setCount(initialCount);
    }
  }, [initialCount, isRunning]);

  useEffect(() => {
    if (!isRunning || count <= 0) return;

    const intervalId = setInterval(() => {
      if (!isEnabled) return;
      setCount((prevCount) => {
        const newCount = prevCount - 1;

        if (onTickRef.current) {
          onTickRef.current(newCount);
        }

        if (newCount <= 0) {
          setIsRunning(false);
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }

        return newCount;
      });
    }, interval);

    return () => clearInterval(intervalId);
  }, [isRunning, count, interval, isEnabled]);

  const startTimer = useCallback(() => {
    hasStartedRef.current = true;
    setIsRunning(true);
  }, []);

  const stopTimer = useCallback(() => {
    setIsRunning(false);
  }, []);

  const resetTimer = useCallback(() => {
    setCount(initialCount);
    setIsRunning(false);
    hasStartedRef.current = false;
  }, [initialCount]);

  const pauseTimer = useCallback(() => {
    setIsRunning(false);
  }, []);

  return {
    count,
    isRunning,
    startTimer,
    stopTimer,
    pauseTimer,
    resetTimer,
  };
};

export default useTimer;
